import React, { useMemo, useState, useEffect, useCallback } from 'react';
import AccordionContext from './accordion-context';
import Collapse from './accordion-collapse';
import Toggle from './accordion-toggle';

export type AccordionPropsType = {
    element?: React.ElementType;
    elementRef?: React.RefObject<HTMLDivElement | HTMLUListElement>;
    activeEventKey?: string | number | null;
    onToggle?: (event?: any) => void;
    children?: React.ReactNode | React.ReactNode[];
};

export type AccordionType = React.FC<AccordionPropsType> & {
    Toggle: typeof Toggle;
    Collapse: typeof Collapse;
};

export type UseAccordionEventKeyType = (
    eventKey: AccordionPropsType['activeEventKey'],
    onToggle: AccordionPropsType['onToggle']
) => void;

const useAccordionEventKey: UseAccordionEventKeyType = (eventKey, onToggle) => {
    const [activeEventKey, setActiveEventKey] = useState(eventKey);

    useEffect(() => {
        setActiveEventKey(eventKey);
    }, [eventKey, onToggle]);

    return [activeEventKey, setActiveEventKey];
};

const Accordion: AccordionType = ({
    element: Component = 'div',
    elementRef,
    activeEventKey,
    onToggle,
    children,
}) => {
    // @ts-ignore
    const [eventKey, setEventKey] = useAccordionEventKey(activeEventKey, onToggle);

    const handleToggle = useCallback(
        (dispatchedEventKey: AccordionPropsType['activeEventKey']) => {
            if (activeEventKey !== undefined) {
                if (onToggle) {
                    onToggle(dispatchedEventKey);
                }

                return;
            }
            setEventKey(dispatchedEventKey);
        },
        [activeEventKey, onToggle, setEventKey]
    );

    const context = useMemo(() => {
        return {
            activeEventKey: eventKey,
            onToggle: handleToggle,
        };
    }, [eventKey, handleToggle]);

    return (
        <AccordionContext.Provider value={context}>
            <Component ref={elementRef}>{children}</Component>
        </AccordionContext.Provider>
    );
};

Accordion.Toggle = Toggle;
Accordion.Collapse = Collapse;

export default Accordion;
